<template>
  <div class="modal-overlay" @click.self="$emit('cancel')">
    <div class="confirm-modal">
      <div class="modal-header">
        <h2>Confirm</h2>
        <button class="close-btn" @click="$emit('cancel')">×</button>
      </div>      
      <p class="modal-subtitle">Please review your changes before submitting</p>      
      <div v-for="disb in props.changes" :key="disb.disbursementId" class="disbursement-changes">        
        <table v-if="disb.fieldChanges && disb.fieldChanges.length > 0" class="changes-table">
          <thead>
            <tr>
              <th>Cert. Seq. {{ disb.certSeq }}{{ disb.isNew ? '' : `/Disb. Seq. ${disb.disbSeq}` }}</th>
              <th>PREVIOUS</th>
              <th class="new-column new-label">NEW</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="change in disb.fieldChanges" :key="change.field">
              <td class="field-name">{{ change.field }}</td>
              <td>{{ change.previous }}</td>
              <td class="new-column">{{ change.new }}</td>
            </tr>
          </tbody>
        </table>        
        <div v-if="disb.postDisbChange" class="post-disb-section">
          <h4 class="section-title">
            Post Disbursement Change{{ disb.postDisbChange.isEdit ? ' - Updates' : '' }} for Cert. Seq. {{ disb.certSeq }}{{ disb.isNew ? '' : `/Disb. Seq. ${disb.disbSeq}` }}
          </h4>          
          <table v-if="disb.postDisbChange.isEdit && disb.postDisbChange.changes" class="changes-table">
            <thead>
              <tr>
                <th></th>
                <th>PREVIOUS</th>
                <th class="new-column">NEW</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="change in disb.postDisbChange.changes" :key="change.field">
                <td class="field-name">{{ change.field }}</td>
                <td>{{ change.previous }}</td>
                <td class="new-column">{{ change.new }}</td>
              </tr>
            </tbody>
          </table>
          <table v-else class="single-column-table">
            <tbody>              
              <tr>
                <td class="field-name">Disb. Amount</td>
                <td class="field-value">{{ disb.postDisbChange.newAmount }}</td>
              </tr>
              <tr>
                <td class="field-name">Processing Date</td>
                <td class="field-value">{{ disb.postDisbChange.processingDate }}</td>
              </tr>
              <tr>
                <td class="field-name">Effective Date</td>
                <td class="field-value">{{ disb.postDisbChange.effectiveDate }}</td>
              </tr>
              <tr v-if="disb.postDisbChange.settleDate">
                <td class="field-name">Settle Date</td>
                <td class="field-value">{{ disb.postDisbChange.settleDate }}</td>
              </tr>
              <tr v-if="disb.postDisbChange.method">
                <td class="field-name">Method</td>
                <td class="field-value">{{ disb.postDisbChange.method }}</td>
              </tr>
              <tr>
                <td class="field-name">Post Disb. Change Reason</td>
                <td class="field-value">{{ disb.postDisbChange.reason }}</td>
              </tr>
            </tbody>
          </table>
        </div>        
        <div v-if="disb?.isNew && disb.newDisbursement" class="new-disb-section">
          <h4 class="section-title new-label">NEW DISBURSEMENT</h4>
          <table class="single-column-table">
            <tbody>
              <tr>
                <td class="field-name">Status</td>
                <td class="field-value">{{ disb.newDisbursement.status }}</td>
              </tr>
              <tr>
                <td class="field-name">Amount</td>
                <td class="field-value">{{ disb.newDisbursement.amount }}</td>
              </tr>
              <tr>
                <td class="field-name">Date</td>
                <td class="field-value">{{ disb.newDisbursement.date }}</td>
              </tr>
              <tr>
                <td class="field-name">Method</td>
                <td class="field-value">{{ disb.newDisbursement.method }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>      
      <div class="modal-footer">
        <label class="checkbox-label" v-if="props.canGenerateModificationFile">
          <input type="checkbox" v-model="generateModFile" />
          Generate Modification File
        </label>
        
        <div class="modal-actions">
          <button class="btn-cancel" @click="$emit('cancel')">Cancel</button>
          <button 
            class="btn-confirm" 
            @click="$emit('confirm', generateModFile)"
            :disabled="props.isSaving"
          >
            {{ props.isSaving ? 'Saving...' : 'Confirm Changes' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { DisbursementChangesSummary } from '@/types/disbursements';

const props = defineProps<{
  changes: DisbursementChangesSummary[];
  isSaving?: boolean;
  canGenerateModificationFile?: boolean;
}>();

defineEmits<{
  (e: 'confirm', generateModFile: boolean): void;
  (e: 'cancel'): void;
}>();

const generateModFile = ref(props.canGenerateModificationFile !== false);
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.confirm-modal {
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 70%;
  min-width: 50%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.modal-header h2 {
  margin: 0;
  font-size: 24px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  line-height: 1;
}

.close-btn:hover {
  color: #000;
}

.modal-subtitle {
  margin-bottom: 18px;
  font-weight: 500;
}

.disbursement-changes {
  margin-bottom: 24px;  
}

.disbursement-changes:last-of-type {
  border-bottom: none;
}

.disbursement-changes h3 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
}

/* Two-column table for field changes */
.changes-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
  table-layout: fixed;
}

.changes-table th,
.changes-table td {
  padding: 12px;
  text-align: left;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 300;
}

.changes-table th:first-child,
.changes-table td:first-child {
  width: 40%;
}

.changes-table th:nth-child(2),
.changes-table td:nth-child(2) {
  width: 30%;
}

.changes-table th:nth-child(3),
.changes-table td:nth-child(3) {
  width: 30%;
}

.changes-table thead th {
  font-weight: 600;  
}

.changes-table .field-name {
  font-weight: 700;
  border-right: 1px solid #e0e0e0;
}

.changes-table .new-column {  
  font-weight: 500;
  border-left: 1px solid #e0e0e0;
}

/* Single-column table for post disb change */
.post-disb-section {
  margin-top: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #666;
}

.single-column-table {
  width: 100%;
  border-collapse: collapse;
}

.single-column-table td {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.single-column-table .field-name {
  font-weight: 700;
  width: 40%;
  border-right: 1px solid #e0e0e0;
}

.single-column-table .field-value {  
  font-weight: 500;
  border-left: 1px solid #e0e0e0;
}

.modal-footer {
  margin-top: 24px;  
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  cursor: pointer;
  font-weight: 300;
}

.checkbox-label input[type="checkbox"] {  
  cursor: pointer;
  accent-color: #000;  
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel,
.btn-confirm {
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid #ccc;
  transition: all 0.2s;
}

.btn-cancel {
  background: white;
  color: #333;
}

.btn-cancel:hover {
  background: #f5f5f5;
}

.btn-confirm {
  background: #4caf50;
  color: white;
  border-color: #4caf50;
}

.btn-confirm:hover {
  background: #45a049;
}

.new-disb-section {
  margin-bottom: 16px;
}

.new-label {
  color: #45a049;
  font-weight: 700;
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>